import React from 'react';
import {useDispatch} from 'react-redux';
import {
    ActionButton,
    DefaultButton,
    Dialog,
    DialogFooter,
    FontSizes,
    Icon,
    IconButton,
    Link,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
    useTheme,
} from '@fluentui/react';
import {toast} from 'react-toastify';
import api, {LIST, STOREFRONT_SETTINGS, useBulkListOnStorefrontMutation} from '../../../store/Api';
import {
    assistantErrorMessage,
    useApproveAssistantPublishMutation,
    useGetAssistantSessionQuery,
    useRequestAssistantNotificationMutation,
    useSendAssistantMessageMutation,
    useTryAgainAssistantSessionMutation,
} from '../../../store/endpoints/AssistantEndpoints';
import {ASSISTANT_STATUS, assistantSessionIsRunning} from '../../../models/Assistant';
import {IStorefrontSuggestedBundle, IStorefrontSuggestedProduct} from '../../../models/Storefront';
import {
    useGetStorefrontProductSuggestionsQuery,
    useGetStorefrontsQuery,
    useListBundleOnStorefrontMutation,
    usePatchStorefrontMutation,
} from '../../../store/endpoints/StorefrontSettingsEndpoints';
import {
    isStorefrontLeaseConflict,
    STOREFRONT_LEASE_CONFLICT_MESSAGE,
    storefrontSaveErrorMessage,
} from '../../../logic/storefrontLeaseConflict';
import {
    addErrorMessage,
    listingFailureMessage,
    suggestionListingBody,
} from '../suggestedProductsLogic';
import DetailsModal from '../../DetailsModal/DetailsModal';
import StorefrontBundleModal from '../../ProductBundles/StorefrontBundleModal';
import StorefrontPreviewButton from '../../AdaptiveIntelligence/Assistant/StorefrontRestyle/StorefrontPreviewButton';
import AiCancelSurveyDialog from './AiCancelSurveyDialog';
import WizardSetupTab, {WizardSetupSaveState} from './WizardSetupTab';
import {WizardBundlesTab, WizardProductsTab} from './WizardSuggestionsTab';
import {IWizardSetupAnswers, setupPatchBody, setupValidationError} from './wizardSetupLogic';
import {currentActivityLabel} from './aiStorefrontCreateLogic';

interface IAiGenerationProgressProps {
    sessionId: string;
    /** Leave the flow open in the background. The generation keeps running server-side. */
    onDismiss: () => void;
    /** The session was cancelled; the caller sends the user back to the template picker. */
    onStartOver: () => void;
    /** The storefront was approved and published. */
    onCompleted: (storefrontId?: string) => void;
    /**
     * Try Again produced a replacement session; the host should remount the dialog on it. The old
     * session is cancelled and its storefront deleted by then, so there is nothing left to go back to.
     */
    onSwitchSession?: (newSessionId: string) => void;
}

const PUBLISH_FAILED = 'The storefront could not be published.';
const CHANGES_FAILED = 'Your changes could not be sent.';
const TRY_AGAIN_FAILED = 'We could not start this build over.';
const TRY_AGAIN_NO_SESSION = 'We started a new build but could not open it. Reload the storefronts page to find it.';
const ADD_PRODUCT_FAILED = 'We could not add that product to your storefront.';
const ADD_BUNDLE_FAILED = 'We could not add that bundle to your storefront.';
const NO_CATALOG = 'There is no product catalog on this organization yet, so there is nothing to add.';
const SETUP_FAILED = 'Your store setup could not be saved.';
const SETUP_BLOCKS_PUBLISH = 'Your store setup could not be saved, so nothing was published. Check the Store Setup step and publish again.';
const SETUP_INVALID = 'Finish the questions on the Store Setup step before publishing.';
const NOTIFY_FAILED = 'We could not arm the email notification.';

/**
 * How long the setup answers sit still before they are written.
 *
 * Long enough that typing an email address is one save rather than thirty, short enough that an operator
 * who answers a question and moves to the next step has already had it saved by the time they get there.
 */
const AUTO_SAVE_DELAY = 1500;

/**
 * How many suggestions the wizard fetches, and how many of them it lists before anyone searches.
 *
 * The fetched set is the API's maximum for both, because the search on each step boosts and filters the
 * whole set rather than the visible slice: a suggestion the operator went looking for should be found with
 * its reason chip intact instead of turning up as an anonymous catalog row. What is listed unsearched is
 * still a handful, which is enough to scroll through and not enough to be a chore.
 */
const PRODUCT_SUGGESTION_SIZE = 48;
const BUNDLE_SUGGESTION_SIZE = 12;
const PRODUCT_DISPLAY_COUNT = 12;
const BUNDLE_DISPLAY_COUNT = 6;

/**
 * The lists handed to the steps before the suggestions land.
 *
 * Module constants rather than a `?? []` at the call site: the steps memoize the boosted-id list against
 * this array's identity, and a fresh empty array on every poll would restart the search debounce forever.
 */
const NO_SUGGESTED_PRODUCTS: IStorefrontSuggestedProduct[] = [];
const NO_SUGGESTED_BUNDLES: IStorefrontSuggestedBundle[] = [];

/**
 * The dialog's frame.
 *
 * Fixed, in every state and on every step. The steps carry wildly different amounts of content (four
 * questions, a dozen product rows, a review panel), and a dialog that resizes to each of them moves
 * the footer buttons and the step indicator out from under the pointer between clicks. The body is
 * the only part that gives: it scrolls.
 *
 * The height is capped against the viewport too, but well under it: the title and the footer sit
 * outside this region, so a cap at the full 85vh the design asks for would push the buttons off a
 * short screen.
 */
const DIALOG_WIDTH = 860;
const FRAME_HEIGHT = 600;
const FRAME_MAX_HEIGHT = '70vh';

/** Which step is showing. Adding a fifth is a key here and an entry in the steps array below. */
type WizardStepKey = 'setup' | 'products' | 'bundles' | 'review';

/**
 * One step of the wizard.
 *
 * The array of these drives both the indicator and the body, so a new step is one entry and nothing
 * else: no second list to keep in order, and no switch to extend.
 */
interface IWizardStep {
    key: WizardStepKey;
    label: string;
    /** The progress affordance drawn after the label: a tick, a count, something to look at. */
    badge?: React.ReactNode;
    render: () => React.ReactNode;
}

/**
 * How a setup save ended. More than a boolean because the caller cares which failure it was: a lease
 * conflict is a "try again in a moment", an invalid answer is the user's to fix, and nothing to save
 * is not a failure at all.
 */
type SetupSaveOutcome = 'saved' | 'nothing' | 'queued' | 'invalid' | 'error';

/**
 * Fluent's DialogFooter is a right-aligned flex row that wraps each child in a span, and it zeroes
 * the font size so the spans cannot pick up whitespace. The footer here is one full-width row
 * instead: the single wrapper has to grow to the dialog's width, its own `0 4px` margin has to go so
 * the row lines up with the body, and the font size has to come back or the `1em` gaps inside
 * measure nothing at all.
 */
const FOOTER_STYLES = {
    actionsRight: {marginRight: 0},
    action: {margin: 0, display: 'block', flexGrow: 1, minWidth: 0, fontSize: FontSizes.medium},
};

/**
 * The black box: a progress display while the assistant works, with work to do underneath it.
 *
 * The turn runs in the LLM worker rather than in the request that queued it, so the session is
 * polled while it is Pending or Processing and left alone otherwise. As in the chat dock, polling
 * is switched off by setting the interval to zero rather than by skipping the query: skipping would
 * drop the cached session and blank this dialog the instant the work finished.
 *
 * What it deliberately does not show is the transcript. The tool calls and the model's chatter are
 * not the product here; a storefront that looks like the user's website is.
 *
 * The wait used to be dead time ending in a single "add these products?" step after publishing. It is
 * now a four-step wizard the operator can work through from the moment the storefront record exists,
 * which is the first poll, long before any design has been applied. The storefront's own id is
 * available that early, and neither the suggestions read nor the product and bundle listing writes are
 * guarded by the assistant's edit lease, so all of that works mid-generation. Settings writes are
 * guarded, which is why the setup answers are a queued save rather than a live one.
 *
 * The steps never advance themselves. The session changing state under a user who is mid-sentence in
 * a text field, or halfway to an Add button, would move the thing they were aiming at; the badge on
 * Review is how they are told there is something waiting for them there.
 */
const AiGenerationProgress: React.FC<IAiGenerationProgressProps> = (props) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [sendMessage] = useSendAssistantMessageMutation();
    const [approvePublish] = useApproveAssistantPublishMutation();
    const [tryAgainSession] = useTryAgainAssistantSessionMutation();
    const [patchStorefront] = usePatchStorefrontMutation();
    const [bulkList] = useBulkListOnStorefrontMutation();
    const [listBundle] = useListBundleOnStorefrontMutation();

    const [running, setRunning] = React.useState(true);
    const sessionQuery = useGetAssistantSessionQuery(props.sessionId, {
        pollingInterval: running ? 3000 : 0,
        refetchOnMountOrArgChange: true,
    });
    const session = sessionQuery.data;
    const status = session?.status;

    React.useEffect(() => {
        if (sessionQuery.isUninitialized || sessionQuery.isLoading) return;
        setRunning(assistantSessionIsRunning(status));
    }, [status, sessionQuery.isUninitialized, sessionQuery.isLoading]);

    const [changes, setChanges] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [approving, setApproving] = React.useState(false);
    const [tryingAgain, setTryingAgain] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
    const [surveyOpen, setSurveyOpen] = React.useState(false);
    const [activeStep, setActiveStep] = React.useState<WizardStepKey>('setup');

    // The generation creates its own storefront; targetId is the fallback for a session that was
    // pointed at one that already existed. Populated from the very first poll, before any AI work.
    const storefrontId = session?.createdStorefrontId ?? session?.targetId;

    // Name the storefront this dialog is operating on. An organization can have several builds with
    // near-identical names going at once, and the deep link does not promise which session it lands
    // on, so every claim and every destructive action in here says which store it means. The list is
    // the same cached query the storefronts page holds; the subUrl is the disambiguator.
    const storefronts = useGetStorefrontsQuery();
    const storefrontRow = storefronts.data?.items?.find((s) => s.id === storefrontId);
    const storefrontLabel = storefrontRow
        ? `${storefrontRow.name} (${storefrontRow.liveSubUrl ?? storefrontRow.subUrl})`
        : session?.title;

    // "Live" is only ever the published flag. A session can report Completed without anything having
    // been published (the model ended its turn without marking the work ready), and telling the
    // operator their store is live when it is not would be worse than any other mistake this dialog
    // can make. An unpublished Completed session renders as review instead.
    const completed = status === ASSISTANT_STATUS.COMPLETED && session?.published === true;
    const awaitingApproval = status === ASSISTANT_STATUS.AWAITING_APPROVAL
        || (status === ASSISTANT_STATUS.COMPLETED && session?.published !== true);
    const failed = status === ASSISTANT_STATUS.FAILED;
    const cancelled = status === ASSISTANT_STATUS.CANCELLED;
    const working = running || (!awaitingApproval && !failed && !cancelled && !completed);

    // What the footer spinner says: the tool the assistant most recently ran, in the user's words.
    // The session document is saved after every tool execution, so this is a live account of the turn
    // rather than a timer walking through invented copy. It changes when a poll lands and at no other
    // time, which is also why there is no interval left in this component.
    const spinnerLabel = currentActivityLabel(session);

    // --- Store setup answers -----------------------------------------------------------------------
    // Gathered during the wait and written with one sparse PATCH. The assistant only ever writes styling
    // fields, so nothing here can be overwritten by the turn in flight; the only conflict is timing, and
    // the API answers 409 rather than writing while the turn holds the edit lease.
    const [answers, setAnswersState] = React.useState<IWizardSetupAnswers>({});
    const [setupDirty, setSetupDirty] = React.useState(false);
    const [setupState, setSetupState] = React.useState<WizardSetupSaveState>('unsaved');
    // The save is fired from a debounce timer and from two effects, never from a button, so the latest
    // answers are read from a ref rather than from a closure that may be a render behind.
    const answersRef = React.useRef<IWizardSetupAnswers>(answers);
    const setupSavingRef = React.useRef(false);

    /**
     * Write the answered setup questions.
     *
     * A 409 is not an error here, it is a "not yet": the assistant holds the edit lease for the length of
     * a turn, and the answers are perfectly good, so the save is parked as queued and replayed by the
     * effect below the moment the session stops running. Nothing is toasted for that case, because being
     * told a save failed when it is simply waiting reads as data loss.
     *
     * Silent is what the autosave asks for. A save nobody pressed a button for should not narrate itself:
     * no "saving", no "saved", and the step's own status line carries both. A genuine failure still speaks
     * up, because the answers really are not on the storefront and nothing else in the dialog would say so.
     */
    const saveSetup = React.useCallback(async (options?: {silent?: boolean}): Promise<SetupSaveOutcome> => {
        const current = answersRef.current;
        if (setupValidationError(current)) {
            setSetupState('unsaved');
            return 'invalid';
        }
        const patch = setupPatchBody(current);
        if (!patch || !storefrontId) {
            setSetupDirty(false);
            return 'nothing';
        }
        if (setupSavingRef.current) return 'error';
        setupSavingRef.current = true;
        setSetupState('saving');
        const toastId = options?.silent === true ? undefined : toast.loading('Saving storefront setup...');
        try {
            const result = await patchStorefront({id: storefrontId, patch}).unwrap();
            if (result?.success === false) throw new Error(result.message || SETUP_FAILED);
            if (toastId !== undefined) {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Storefront setup saved',
                    isLoading: false,
                    autoClose: 3000,
                });
            }
            setSetupState('saved');
            setSetupDirty(false);
            return 'saved';
        } catch (error) {
            if (isStorefrontLeaseConflict(error)) {
                // Waiting, not failing. The tab says so; the toast would say otherwise.
                if (toastId !== undefined) toast.dismiss(toastId);
                setSetupState('queued');
                return 'queued';
            }
            const message = storefrontSaveErrorMessage(error, addErrorMessage(error, SETUP_FAILED));
            if (toastId !== undefined) {
                toast.update(toastId, {type: 'error', render: message, isLoading: false, autoClose: 4000});
            } else {
                toast.error(message, {autoClose: 4000});
            }
            setSetupState('unsaved');
            return 'error';
        } finally {
            setupSavingRef.current = false;
        }
    }, [patchStorefront, storefrontId]);

    // --- The autosave -------------------------------------------------------------------------------
    // There is no Save button on the setup step any more, so a pause in the typing is what commits the
    // answers. Everything below exists to keep that from becoming a nuisance: one timer restarted by every
    // keystroke, nothing sent while an answer is invalid or the storefront record does not exist yet, and
    // a hold after a real failure so a broken save is not re-reported on every subsequent character.
    const autoSaveTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
    const autoSaveBlockedRef = React.useRef(false);
    // The timer outlives the render that armed it, and both of these change under it: the storefront id
    // arrives on the first poll, and saveSetup is rebuilt when it does. A closure would still be waiting
    // for a storefront that already exists, so the callback reads the live pair instead.
    const storefrontIdRef = React.useRef(storefrontId);
    storefrontIdRef.current = storefrontId;
    const saveSetupRef = React.useRef(saveSetup);
    saveSetupRef.current = saveSetup;

    const cancelAutoSave = () => {
        if (autoSaveTimerRef.current !== undefined) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = undefined;
        }
    };

    const scheduleAutoSave = () => {
        cancelAutoSave();
        autoSaveTimerRef.current = setTimeout(() => {
            autoSaveTimerRef.current = undefined;
            // A save that failed for real waits for the answers to change before trying again.
            if (autoSaveBlockedRef.current) return;
            // Two reasons to come back later rather than give up: the storefront record lands on the
            // first poll and may not exist yet, and a save already in flight should not be raced. Both
            // resolve on their own, and the answers stay dirty in the meantime.
            if (!storefrontIdRef.current || setupSavingRef.current) {
                scheduleAutoSave();
                return;
            }
            // Half-typed answers are not failures. The field already carries the message; sending the
            // patch anyway would turn it into a toast the operator never asked for.
            if (setupValidationError(answersRef.current)) return;
            void saveSetupRef.current({silent: true}).then((outcome) => {
                if (outcome === 'error') autoSaveBlockedRef.current = true;
            });
        }, AUTO_SAVE_DELAY);
    };

    // A dialog closed mid-pause must not fire its timer into an unmounted tree.
    React.useEffect(() => () => {
        if (autoSaveTimerRef.current !== undefined) clearTimeout(autoSaveTimerRef.current);
    }, []);

    const changeAnswers = (next: IWizardSetupAnswers) => {
        answersRef.current = next;
        setAnswersState(next);
        setSetupDirty(true);
        // An edit after a successful save means the storefront no longer holds what the form shows. A
        // queued save stays queued: the flush that eventually runs should carry the newer answers too.
        setSetupState((current) => (current === 'saved' ? 'unsaved' : current));
        // The answers moved, so whatever went wrong last time is worth one more attempt.
        autoSaveBlockedRef.current = false;
        scheduleAutoSave();
    };

    // The queued flush. The lease is free whenever the session is not mid-turn, so this fires at
    // AwaitingApproval, after a publish, and after a failure. Never on a cancelled session: cancelling
    // deletes the storefront, so there would be nothing left to patch.
    //
    // Once per pause, tracked by the ref. A flush that is refused again leaves the state at queued,
    // which would otherwise re-trigger this effect immediately and spin; the retry instead waits for
    // the next turn to start and finish, or for the next answer to restart the debounce.
    const autoFlushedRef = React.useRef(false);
    React.useEffect(() => {
        if (running || assistantSessionIsRunning(status)) {
            autoFlushedRef.current = false;
            return;
        }
        if (setupState !== 'queued' || !storefrontId || cancelled) return;
        if (autoFlushedRef.current) return;
        autoFlushedRef.current = true;
        void saveSetup();
    }, [setupState, storefrontId, running, status, cancelled, saveSetup]);

    // --- Completion notification ---------------------------------------------------------------
    // Two mechanisms behind one button, because they cover different absences. The browser
    // Notification is instant but dies with the tab; the email is the one that reaches somebody who
    // closed the page and went to lunch. Arming does both, and the button reads as armed if either
    // took, because to the operator it is one promise: "tell me when it's done".
    //
    // Both are best-effort, and neither blocks the other. A browser that refuses permission still
    // gets the email, which is why the button is no longer gated on notification support.
    const notifySupported = typeof window !== 'undefined' && 'Notification' in window
        && Notification.permission !== 'denied';
    const [notifyArmed, setNotifyArmed] = React.useState(false);
    const [emailArmed, setEmailArmed] = React.useState(false);
    const [arming, setArming] = React.useState(false);
    const notifiedRef = React.useRef(false);
    const [requestNotification] = useRequestAssistantNotificationMutation();
    // notifyRequested comes back on every poll, so a dialog reopened from the deep link shows the
    // armed state rather than inviting the operator to arm something they already armed.
    const notificationArmed = notifyArmed || emailArmed || session?.notifyRequested === true;
    const armNotification = async () => {
        setArming(true);
        // The permission prompt has to happen inside the user gesture, so it goes first.
        if (notifySupported) {
            try {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') setNotifyArmed(true);
            } catch {
                // Unsupported or blocked; the email below is still worth arming.
            }
        }
        try {
            const result = await requestNotification(props.sessionId).unwrap();
            if (result?.success === false) throw new Error(result.message ?? NOTIFY_FAILED);
            setEmailArmed(true);
        } catch (error) {
            // One small toast and no more. The browser notification, if it was granted, stays armed:
            // the operator is not worse off than before they pressed it.
            toast.error(assistantErrorMessage(error, NOTIFY_FAILED), {autoClose: 4000});
        } finally {
            setArming(false);
        }
    };
    React.useEffect(() => {
        if (!notifyArmed || notifiedRef.current) return;
        if (!(awaitingApproval || completed || failed)) return;
        if (document.visibilityState !== 'hidden') return;
        try {
            notifiedRef.current = true;
            const note = new Notification(
                failed ? 'Your storefront build hit a problem' : 'Your storefront is ready',
                {body: `${storefrontLabel ?? 'Your storefront'} ${failed ? 'could not finish building.' : 'is ready for review.'}`});
            note.onclick = () => window.focus();
        } catch {
            // A notification is sugar; never let it throw into React.
        }
    }, [notifyArmed, awaitingApproval, completed, failed, storefrontLabel]);

    const requestChanges = async () => {
        const message = changes.trim();
        if (!message) return;
        setSending(true);
        setErrorMessage(undefined);
        try {
            const result = await sendMessage({id: props.sessionId, message}).unwrap();
            if (result?.success === false) {
                setErrorMessage(result.message ?? CHANGES_FAILED);
            } else {
                setChanges('');
                // The API queues the turn, so the session is Pending again. Resume polling now
                // rather than waiting for the next poll to notice.
                setRunning(true);
            }
        } catch (error) {
            setErrorMessage(assistantErrorMessage(error, CHANGES_FAILED));
        }
        setSending(false);
    };

    /**
     * Publish, but flush the setup answers first.
     *
     * Order matters and is not cosmetic. Settings patched before the approve-publish are promoted to the
     * live document along with the design; patched after it, they land on the preview copy and the shopper
     * never sees them. So an unsaved or queued answer blocks the publish rather than racing it.
     */
    const approve = async () => {
        setApproving(true);
        setErrorMessage(undefined);

        // The publish flush is the autosave's flush too: it reads the same ref, so a timer still ticking
        // would only write the same answers a second time. Cancel it and let this one do the work.
        cancelAutoSave();

        if (setupDirty || setupState === 'queued') {
            const outcome = await saveSetup();
            if (outcome !== 'saved' && outcome !== 'nothing') {
                // The one place a step change is not the user's doing, and it is still their click that
                // caused it: they pressed Publish and the answer is on another step.
                setActiveStep('setup');
                setErrorMessage(
                    outcome === 'invalid' ? SETUP_INVALID
                        : outcome === 'queued' ? STOREFRONT_LEASE_CONFLICT_MESSAGE
                            : SETUP_BLOCKS_PUBLISH
                );
                setApproving(false);
                return;
            }
        }

        const toastId = toast.loading('Publishing your storefront...');
        try {
            await approvePublish(props.sessionId).unwrap();
            toast.update(toastId, {
                type: 'success',
                render: 'Your storefront is live',
                isLoading: false,
                autoClose: 3000,
            });
            dispatch(api.util.invalidateTags([{type: STOREFRONT_SETTINGS, id: LIST}]));
            // Publishing is not the end of the flow: the steps stay open so a late product or bundle can
            // still be added, and the footer switches to Done.
            setRunning(false);
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: assistantErrorMessage(error, PUBLISH_FAILED),
                isLoading: false,
                autoClose: 4000,
            });
        }
        setApproving(false);
    };

    /**
     * Throw this build away and run the whole thing again from the same answers.
     *
     * A testing affordance: the API cancels this session, deletes the storefront it built, and starts
     * a fresh generation, so what comes back is a different session entirely. The host has to remount
     * this dialog on the new id; without an onSwitchSession handler there is nowhere to go, so the
     * user is told rather than left polling a cancelled session.
     */
    const tryAgain = async () => {
        setTryingAgain(true);
        setErrorMessage(undefined);
        const toastId = toast.loading('Starting this build over...');
        try {
            const next = await tryAgainSession(props.sessionId).unwrap();
            if (next?.id) {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Building your storefront again from scratch',
                    isLoading: false,
                    autoClose: 3000,
                });
                props.onSwitchSession?.(next.id);
            } else {
                toast.update(toastId, {
                    type: 'error',
                    render: TRY_AGAIN_NO_SESSION,
                    isLoading: false,
                    autoClose: 4000,
                });
                setErrorMessage(TRY_AGAIN_NO_SESSION);
            }
        } catch (error) {
            const message = assistantErrorMessage(error, TRY_AGAIN_FAILED);
            toast.update(toastId, {type: 'error', render: message, isLoading: false, autoClose: 4000});
            setErrorMessage(message);
        }
        setTryingAgain(false);
    };

    // --- Suggestions -------------------------------------------------------------------------------
    // Read as soon as the storefront record exists, which is the first poll, not the publish. One call
    // covers both tabs. No source narrowing: a brand-new store wants every reason the API has, in-stock
    // catalog filler included, and excludeListed drops whatever the generation already seeded onto it.
    const suggestions = useGetStorefrontProductSuggestionsQuery(
        {
            storefront: storefrontId ?? '',
            size: PRODUCT_SUGGESTION_SIZE,
            bundleSize: BUNDLE_SUGGESTION_SIZE,
            excludeListed: true,
            includeOutOfStock: true,
        },
        {skip: !storefrontId, refetchOnMountOrArgChange: true}
    );
    const suggestedProducts = suggestions.data?.products ?? NO_SUGGESTED_PRODUCTS;
    const suggestedBundles = suggestions.data?.bundles ?? NO_SUGGESTED_BUNDLES;
    const catalog = suggestions.data?.catalog;

    const [addedProductIds, setAddedProductIds] = React.useState<string[]>([]);
    const [addedBundleIds, setAddedBundleIds] = React.useState<string[]>([]);
    // One add at a time across both tabs. The writes are idempotent, but a burst of clicks queuing up
    // behind each other is not something the operator asked for.
    const [pendingAddId, setPendingAddId] = React.useState<string | undefined>();

    /**
     * List one suggested product on the storefront, straight away.
     *
     * Nothing is staged: the listing call is additive and idempotent, and a build that gets cancelled
     * takes the whole storefront with it, so there is no half-committed state for a staging step to
     * protect.
     */
    const addProduct = async (product: IStorefrontSuggestedProduct) => {
        if (!storefrontId || pendingAddId || addedProductIds.includes(product.id)) return;
        if (!catalog) {
            toast.error(NO_CATALOG, {autoClose: 4000});
            return;
        }
        const label = product.name ?? 'this product';
        setPendingAddId(product.id);
        const toastId = toast.loading(`Adding ${label} to your storefront...`);
        try {
            const result = await bulkList({
                catalog,
                request: suggestionListingBody(product.id, storefrontId),
            }).unwrap();
            // A business refusal (most often the storefront product limit) arrives as a 200 carrying
            // success false, so the flag is what decides rather than the HTTP status.
            const failure = listingFailureMessage(result, ADD_PRODUCT_FAILED);
            if (failure !== undefined) throw new Error(failure);
            toast.update(toastId, {
                type: 'success',
                render: `Added ${label} to your storefront`,
                isLoading: false,
                autoClose: 3000,
            });
            setAddedProductIds((current) => [...current, product.id]);
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: addErrorMessage(error, ADD_PRODUCT_FAILED),
                isLoading: false,
                autoClose: 4000,
            });
        }
        setPendingAddId(undefined);
    };

    /** The bundle twin. One route, taking the bundle id and nothing else, and just as idempotent. */
    const addBundle = async (bundle: IStorefrontSuggestedBundle) => {
        if (!storefrontId || pendingAddId || addedBundleIds.includes(bundle.id)) return;
        const label = bundle.name ?? 'this bundle';
        setPendingAddId(bundle.id);
        const toastId = toast.loading(`Adding ${label} to your storefront...`);
        try {
            const result = await listBundle({storefront: storefrontId, bundleId: bundle.id}).unwrap();
            if (result?.success === false) throw new Error(result.message || ADD_BUNDLE_FAILED);
            toast.update(toastId, {
                type: 'success',
                render: `Added ${label} to your storefront`,
                isLoading: false,
                autoClose: 3000,
            });
            setAddedBundleIds((current) => [...current, bundle.id]);
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: addErrorMessage(error, ADD_BUNDLE_FAILED),
                isLoading: false,
                autoClose: 4000,
            });
        }
        setPendingAddId(undefined);
    };

    // The one details modal each kind of suggestion opens, hosted here so a row can stay presentational.
    const [detailsTarget, setDetailsTarget] = React.useState<{id: string; catalog: string} | undefined>();
    const [openBundleId, setOpenBundleId] = React.useState<string | undefined>();

    const busy = sending || approving || tryingAgain || setupState === 'saving';
    // Two states take the dialog over instead of stepping through it. A cancelled session has had its
    // storefront deleted, so there is nothing left to set up or stock. A failure blocks the steps too:
    // the one thing worth doing there is deciding what to do about the failure, and a row of steps
    // under the error invites the operator to keep stocking a store that may never be built. The
    // answers already typed survive in component state, so a retry that recovers hands them back.
    const blocked = cancelled || failed;

    // A constant name rather than a state readout: the big state titles live in the body now (the
    // Review step and the blocking states), the footer spinner narrates progress on every step, and a
    // chrome title that repeated them read as saying everything twice.
    const title = 'Create with Adaptive Intelligence';

    // --- The steps ---------------------------------------------------------------------------------

    /** How many of a step's things the operator has done, shown on its label. Nothing at zero. */
    const countBadge = (count: number) => (count > 0
        ? <Text variant='small' styles={{root: {marginLeft: 4}}}>({count})</Text>
        : undefined);

    /**
     * "Anything you would like changed?", as a chat line rather than a form.
     *
     * A labelled multiline box with a Make these changes button under it read as paperwork; the same
     * thing with the send action beside it reads as somewhere to talk to the build, which is what it is.
     * It is present but dead while the AI is mid-turn, so it does not appear from nowhere at the end.
     */
    const renderChangeRequest = (disabled: boolean) => (
        <Stack
            horizontal
            verticalAlign='center'
            tokens={{childrenGap: '0.5em'}}
            styles={{root: {width: '100%', maxWidth: 560}}}
        >
            <Stack.Item grow styles={{root: {minWidth: 0}}}>
                <TextField
                    multiline
                    autoAdjustHeight
                    resizable={false}
                    rows={2}
                    value={changes}
                    disabled={disabled || busy}
                    placeholder='Any changes you would like to request?'
                    onChange={(_, val) => setChanges(val ?? '')}
                    styles={{fieldGroup: {borderRadius: 8}}}
                />
            </Stack.Item>
            <IconButton
                iconProps={{iconName: 'Send'}}
                title={sending ? 'Sending your request...' : 'Send this change request'}
                ariaLabel='Send this change request'
                disabled={disabled || busy || changes.trim() === ''}
                onClick={requestChanges}
            />
        </Stack>
    );

    /**
     * The last step: everything about the build itself.
     *
     * It is one step rather than a state-driven footer because the three things the operator does with
     * a build (look at it, ask for changes, publish it) all belong together, and because a button that
     * appears in the footer the moment a poll lands is a button that arrives under a pointer aimed at
     * something else. Which of them are offered still depends on where the session is; where they are
     * does not.
     */
    const renderReview = () => (
        <Stack
            horizontalAlign='center'
            tokens={{childrenGap: '1em'}}
            styles={{root: {paddingTop: '2em', minWidth: 0, width: '100%'}}}
        >
            <Text variant='xLarge' styles={{root: {fontWeight: 600, textAlign: 'center'}}}>
                {completed
                    ? 'Your storefront is live'
                    : awaitingApproval
                        ? 'Your Storefront is Ready for Review!'
                        : 'Your store is being generated'}
            </Text>

            {/* The wait's one affordance, in the place the Preview button takes once there is something
              * to preview. It is not offered afterwards: by then the answer it would deliver is on screen. */}
            {working && (
                <ActionButton
                    iconProps={{iconName: 'Ringer'}}
                    text={notificationArmed ? 'We\'ll notify you when it\'s done' : 'Notify me when it\'s done'}
                    disabled={notificationArmed || arming}
                    onClick={armNotification}
                />
            )}

            {(awaitingApproval || completed) && (
                <StorefrontPreviewButton
                    storefrontId={storefrontId}
                    text={completed ? 'Open my storefront' : 'Preview'}
                    primary={awaitingApproval}
                    hideIcon={awaitingApproval}
                    primaryDisabled={busy}
                />
            )}

            {/* Shown greyed while the build runs rather than hidden: the operator can see what this step
              * will offer them, so nothing appears under their pointer when the poll finally lands. */}
            {(working || awaitingApproval) && renderChangeRequest(working)}

            {(working || awaitingApproval) && (
                <>
                    {/* The divide. Asking for a change and publishing are opposite decisions and should
                      * not sit a gap apart. */}
                    <Stack.Item styles={{root: {height: '2em'}}}/>
                    <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <ActionButton
                            iconProps={{iconName: 'Rocket'}}
                            text={approving ? 'Publishing...' : 'Publish'}
                            disabled={working || busy}
                            onClick={approve}
                        />
                        <ActionButton
                            iconProps={{iconName: 'Refresh'}}
                            text='Start Over'
                            disabled={working || busy}
                            onClick={() => setSurveyOpen(true)}
                        />
                    </Stack>
                </>
            )}
        </Stack>
    );

    /**
     * The wizard, in order. This array is the only place the order lives: the indicator maps it and the
     * body renders one entry of it, so a fifth step is an entry here and nothing else.
     */
    const steps: IWizardStep[] = [
        {
            key: 'setup',
            label: 'Store Setup',
            badge: setupState === 'saved'
                ? <Icon iconName='CheckMark' styles={{root: {marginLeft: 4}}}/>
                : undefined,
            render: () => (
                <WizardSetupTab
                    answers={answers}
                    onChange={changeAnswers}
                    saveState={setupState}
                />
            ),
        },
        {
            key: 'products',
            label: 'Add Products',
            badge: countBadge(addedProductIds.length),
            render: () => (
                <WizardProductsTab
                    products={suggestedProducts}
                    displayCount={PRODUCT_DISPLAY_COUNT}
                    catalog={catalog}
                    // The storefront record lands on the first poll and the suggestions are read against
                    // it. Until it exists there is nothing to be empty, so the step waits rather than
                    // claiming the catalog had nothing to offer.
                    loading={suggestions.isLoading || !storefrontId}
                    error={suggestions.isError}
                    addedIds={addedProductIds}
                    pendingId={pendingAddId}
                    busy={!!pendingAddId}
                    onAdd={addProduct}
                    onOpenDetails={(product) => {
                        if (catalog) setDetailsTarget({id: product.id, catalog});
                    }}
                />
            ),
        },
        {
            key: 'bundles',
            label: 'Add Bundles',
            badge: countBadge(addedBundleIds.length),
            render: () => (
                <WizardBundlesTab
                    bundles={suggestedBundles}
                    displayCount={BUNDLE_DISPLAY_COUNT}
                    storefrontId={storefrontId}
                    loading={suggestions.isLoading || !storefrontId}
                    error={suggestions.isError}
                    addedIds={addedBundleIds}
                    pendingId={pendingAddId}
                    busy={!!pendingAddId}
                    onAdd={addBundle}
                    onOpenBundle={(bundle) => setOpenBundleId(bundle.id)}
                />
            ),
        },
        {
            key: 'review',
            label: 'Review',
            // The only signal that the session moved on while the operator was working elsewhere. It
            // says "there is something here now"; it does not drag them over to look at it.
            badge: awaitingApproval
                ? (
                    <span
                        title='Your storefront is ready to review'
                        style={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            marginLeft: 6,
                            borderRadius: '50%',
                            verticalAlign: 'middle',
                            background: theme.palette.themePrimary,
                        }}
                    />
                )
                : undefined,
            render: renderReview,
        },
    ];

    const activeIndex = Math.max(0, steps.findIndex((entry) => entry.key === activeStep));
    const step = steps[activeIndex];

    // No wrapping: four short labels fit the fixed width, and without it the gap between them is a CSS
    // rule on the row rather than a wrapper per child, which is what lets the chevrons be fragments.
    const stepIndicator = (
        <Stack horizontal verticalAlign='center' tokens={{childrenGap: '0.5em'}}>
            {steps.map((entry, index) => {
                const current = entry.key === activeStep;
                return (
                    <React.Fragment key={entry.key}>
                        {index > 0 && (
                            <Icon
                                iconName='ChevronRight'
                                styles={{root: {fontSize: 10, color: theme.palette.neutralTertiary}}}
                            />
                        )}
                        <Link
                            onClick={() => setActiveStep(entry.key)}
                            styles={{root: {
                                paddingBottom: 4,
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                fontWeight: current ? 600 : 400,
                                color: current ? theme.palette.neutralPrimary : theme.palette.neutralSecondary,
                                borderBottom: `2px solid ${current ? theme.palette.themePrimary : 'transparent'}`,
                            }}}
                        >
                            {entry.label}
                            {entry.badge}
                        </Link>
                    </React.Fragment>
                );
            })}
        </Stack>
    );

    return (
        <>
            <Dialog
                hidden={false}
                onDismiss={() => {
                    if (!busy) props.onDismiss();
                }}
                // One width for every state. The product rows need most of it (image, name, reason,
                // availability, cost, Add), and a dialog that shrank for the states that do not would
                // move the footer buttons around underneath the operator.
                minWidth={DIALOG_WIDTH}
                maxWidth='90vw'
                dialogContentProps={{
                    title,
                    showCloseButton: !busy,
                }}
                modalProps={{isBlocking: true}}
            >
                {/*
                  * The fixed frame. Everything above the body holds still; the body is the one part
                  * that gives, and it is the only scroll container in the dialog, which is why the
                  * suggestion lists no longer cap their own height.
                  */}
                <Stack
                    tokens={{childrenGap: '1em'}}
                    styles={{root: {height: FRAME_HEIGHT, maxHeight: FRAME_MAX_HEIGHT, minWidth: 0}}}
                >
                    {errorMessage && (
                        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(undefined)}>
                            {errorMessage}
                        </MessageBar>
                    )}

                    {!blocked && stepIndicator}

                    <Stack.Item
                        grow
                        styles={{root: {
                            minHeight: 0,
                            minWidth: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            paddingRight: 4,
                        }}}
                    >
                        {blocked ? (
                            <Stack tokens={{childrenGap: '1em'}} styles={{root: {minWidth: 0}}}>
                                {/*
                                  * The failure, said once. The headline is the whole message; the model's
                                  * own account of what went wrong stays underneath it in small print,
                                  * because it is the only thing here that tells anyone what to do next.
                                  * Two ways forward and no third: pick the build up where it died was
                                  * never the better of them, and offering it made the choice harder.
                                  */}
                                {failed && (
                                    <Stack
                                        horizontalAlign='center'
                                        tokens={{childrenGap: '1em'}}
                                        styles={{root: {paddingTop: '2em', width: '100%'}}}
                                    >
                                        <Text variant='xLarge' styles={{root: {fontWeight: 600, textAlign: 'center'}}}>
                                            There was an error generating your Storefront
                                        </Text>
                                        {session?.error && (
                                            <Text
                                                variant='small'
                                                styles={{root: {
                                                    color: theme.palette.neutralSecondary,
                                                    textAlign: 'center',
                                                    maxWidth: 560,
                                                }}}
                                            >
                                                {session.error}
                                            </Text>
                                        )}
                                        <Stack.Item styles={{root: {height: '2em'}}}/>
                                        <Stack horizontal tokens={{childrenGap: '1em'}}>
                                            <ActionButton
                                                iconProps={{iconName: 'Rerun'}}
                                                text={tryingAgain ? 'Restarting...' : 'Try Again'}
                                                title='Try to generate the store again with the same prompt'
                                                disabled={busy}
                                                onClick={tryAgain}
                                            />
                                            <ActionButton
                                                iconProps={{iconName: 'Refresh'}}
                                                text='Start Over'
                                                disabled={busy}
                                                onClick={() => setSurveyOpen(true)}
                                            />
                                        </Stack>
                                    </Stack>
                                )}

                                {cancelled && (
                                    <Text>
                                        This storefront was removed. You can start again from the template picker.
                                    </Text>
                                )}
                            </Stack>
                        ) : step.render()}
                    </Stack.Item>
                </Stack>

                {/*
                  * One footer row across the dialog: the way back on the left, what the assistant is
                  * doing centered, and the way forward or out on the right, so nothing in this row moves
                  * when the session does. What the operator does about the build lives on the Review
                  * step; the blocking states put their own answer here because they have no steps.
                  */}
                <DialogFooter styles={FOOTER_STYLES}>
                    <Stack
                        horizontal
                        verticalAlign='center'
                        tokens={{childrenGap: '1em'}}
                    >
                        <Stack.Item shrink styles={{root: {minWidth: 0}}}>
                            {!blocked && (
                                <DefaultButton
                                    text='Previous'
                                    iconProps={{iconName: 'ChevronLeft'}}
                                    disabled={activeIndex === 0}
                                    onClick={() => setActiveStep(steps[activeIndex - 1].key)}
                                />
                            )}
                        </Stack.Item>
                        {/* Centered so it reads as the dialog's status rather than as one of the controls. */}
                        <Stack.Item grow styles={{root: {display: 'flex', justifyContent: 'center', minWidth: 0}}}>
                            {working && (
                                <Spinner
                                    size={SpinnerSize.small}
                                    labelPosition='right'
                                    label={spinnerLabel}
                                />
                            )}
                        </Stack.Item>
                        <Stack
                            horizontal
                            verticalAlign='center'
                            tokens={{childrenGap: '1em'}}
                            styles={{root: {flexShrink: 0}}}
                        >
                            {/* Nothing for a failure here: what to do about it is the body's, and the
                              * footer would only offer the same two buttons a second time. Close is
                              * added below for every state that is not already live. */}
                            {/* Already cancelled: nothing left to cancel, so this skips the survey. */}
                            {cancelled && (
                                <PrimaryButton
                                    text='Start over'
                                    iconProps={{iconName: 'Refresh'}}
                                    onClick={props.onStartOver}
                                />
                            )}
                            {/* The store is live by then, so Done is the way out, not a second Close. */}
                            {completed ? (
                                <DefaultButton
                                    text='Done'
                                    iconProps={{iconName: 'CheckMark'}}
                                    disabled={busy}
                                    onClick={() => props.onCompleted(storefrontId)}
                                />
                            ) : (
                                <DefaultButton
                                    text={working ? 'Close and keep building' : 'Close'}
                                    disabled={busy}
                                    onClick={props.onDismiss}
                                />
                            )}
                            {/* Forward is the rightmost thing on the row, chevron trailing to mirror
                              * Previous. Nothing to go on to from the last step: what happens there is
                              * on it. */}
                            {!blocked && activeIndex < steps.length - 1 && (
                                <PrimaryButton
                                    text='Next'
                                    iconProps={{iconName: 'ChevronRight'}}
                                    styles={{flexContainer: {flexDirection: 'row-reverse'}}}
                                    onClick={() => setActiveStep(steps[activeIndex + 1].key)}
                                />
                            )}
                        </Stack>
                    </Stack>
                </DialogFooter>
            </Dialog>

            {detailsTarget && (
                <DetailsModal
                    product={detailsTarget.id}
                    catalog={detailsTarget.catalog}
                    open={true}
                    onClose={() => setDetailsTarget(undefined)}
                />
            )}

            {openBundleId && (
                <StorefrontBundleModal
                    isOpen={true}
                    bundleId={openBundleId}
                    onDismiss={() => setOpenBundleId(undefined)}
                />
            )}

            <AiCancelSurveyDialog
                isOpen={surveyOpen}
                sessionId={props.sessionId}
                storefrontLabel={storefrontLabel}
                onDismiss={() => setSurveyOpen(false)}
                onCancelled={() => {
                    setSurveyOpen(false);
                    props.onStartOver();
                }}
            />
        </>
    );
};

export default AiGenerationProgress;
